/**
 * Route table — the single source of truth for top-level screens, modeled after
 * a React Navigation navigator config so a later migration is mechanical.
 *
 * Only the data-opening pages take params: builder (which project), reader
 * (which comic, and whether shared). Web URLs are derived from this table.
 */

export type RouteName =
  | "ftue"
  | "landing"
  | "builder"
  | "gallery"
  | "sharedGallery"
  | "reader"
  | "coder"
  | "gameLab"
  | "gameTemplates"
  | "animator";

/** Per-route params — only the data-opening pages have any. */
export interface RouteParams {
  ftue: Record<string, never>;
  landing: Record<string, never>;
  builder: { project?: string };
  gallery: Record<string, never>;
  sharedGallery: Record<string, never>;
  reader: { comic: string; shared?: boolean };
  /** Game Coder (hackathon) — see src/coder/. */
  coder: Record<string, never>;
  /** Game Lab welcome screen (hackathon demo) — see src/coder/GameLabScreen.tsx. */
  gameLab: Record<string, never>;
  /** Pick-a-template screen. `template` is carried into the coder page. */
  gameTemplates: Record<string, never>;
  /** Character Animator (hackathon) — see src/animator/. */
  animator: Record<string, never>;
}

export interface Route<N extends RouteName = RouteName> {
  name: N;
  params: RouteParams[N];
}

/** Bare path for each route. */
export const ROUTE_PATH: Record<RouteName, string> = {
  ftue: "/welcome",
  landing: "/",
  builder: "/create",
  gallery: "/my-comics",
  sharedGallery: "/shared",
  reader: "/comic",
  coder: "/game-coder",
  gameLab: "/game-lab",
  gameTemplates: "/game-lab/pick",
  animator: "/animator",
};

const PATH_TO_ROUTE: Record<string, RouteName> = {
  "/welcome": "ftue",
  "/": "landing",
  "/create": "builder",
  "/my-comics": "gallery",
  "/shared": "sharedGallery",
  "/comic": "reader",
  "/game-coder": "coder",
  "/game-lab": "gameLab",
  "/game-lab/pick": "gameTemplates",
  "/animator": "animator",
};

function bare(path: string): string {
  return path.split("?")[0];
}

function getQueryParam(path: string, key: string): string | null {
  const m = path.match(new RegExp(`[?&]${key}=([^&]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Parse a full path+query into a Route. Unknown paths (or reader without a comic) fall back to landing. */
export function routeFromPath(fullPath: string): Route {
  const path = bare(fullPath);
  const name = PATH_TO_ROUTE[path] ?? "landing";

  if (name === "builder") {
    const project = getQueryParam(fullPath, "project");
    return { name, params: project ? { project } : {} };
  }
  if (name === "reader") {
    const comic = getQueryParam(fullPath, "comic");
    if (!comic) return { name: "landing", params: {} };
    const shared = getQueryParam(fullPath, "shared") === "1";
    return { name, params: shared ? { comic, shared: true } : { comic } };
  }
  return { name, params: {} as RouteParams[RouteName] };
}

/** Serialize a Route back into a path+query string (web URL / history entry). */
export function pathFromRoute(route: Route): string {
  const base = ROUTE_PATH[route.name];
  if (route.name === "builder") {
    const { project } = route.params as RouteParams["builder"];
    return project ? `${base}?project=${encodeURIComponent(project)}` : base;
  }
  if (route.name === "reader") {
    const { comic, shared } = route.params as RouteParams["reader"];
    const qs = [`comic=${encodeURIComponent(comic)}`];
    if (shared) qs.push("shared=1");
    return `${base}?${qs.join("&")}`;
  }
  return base;
}
